import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    Input,
    Checkbox,
    Radio,
    Select,
    Card,
    Switch,
    Upload,
    InputNumber,
    Modal,
    Space,
    Typography,
    Row,
    Col,
    message,
    Tag,
    Tooltip,
    Form,
    Table,
    Divider,
    Avatar,
    Empty,
    Popconfirm
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    DragOutlined,
    FileImageOutlined,
    FontSizeOutlined,
    NumberOutlined,
    CheckSquareOutlined,
    ControlOutlined,
    AlignLeftOutlined,
    EyeOutlined,
    SaveOutlined,
    UploadOutlined,
    ArrowLeftOutlined,
    CopyOutlined,
    SettingOutlined,
    FormOutlined,
    CalendarOutlined,
    UserOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import formCreationService from '@/services/formconfiguration.service';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import InspectionBtn from '@/components/InspectionBtn';
import toast from "react-hot-toast";

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

// Form Elements Configuration
const FORM_ELEMENTS = [
    {
        id: 'textbox',
        name: 'Text Input',
        type: 'textbox',
        icon: <FontSizeOutlined />,
        color: '#1890ff'
    },
    {
        id: 'textarea',
        name: 'Text Area',
        type: 'textarea',
        icon: <AlignLeftOutlined />,
        color: '#722ed1'
    },
    {
        id: 'numbertextbox',
        name: 'Number',
        type: 'integer',
        icon: <NumberOutlined />,
        color: '#fa8c16'
    },
    {
        id: 'dropdown',
        name: 'Dropdown',
        type: 'dropdown',
        icon: <ControlOutlined />,
        color: '#52c41a'
    },
    {
        id: 'checkbox',
        name: 'Checkbox',
        type: 'checkbox',
        icon: <CheckSquareOutlined />,
        color: '#13c2c2'
    },
    {
        id: 'radiobutton',
        name: 'Radio Button',
        type: 'radiobutton',
        icon: <ControlOutlined />,
        color: '#eb2f96'
    },
    {
        id: 'image',
        name: 'Image Upload',
        type: 'image',
        icon: <FileImageOutlined />,
        color: '#f5222d'
    }
];

// Options Manager Component
const OptionsManager = ({ options, onChange, elementId }) => {
    const [localOptions, setLocalOptions] = useState(options || []);
    const [newOption, setNewOption] = useState('');

    useEffect(() => {
        setLocalOptions(options || []);
    }, [options]);

    const addOption = () => {
        if (newOption.trim()) {
            const updated = [...localOptions, newOption.trim()];
            setLocalOptions(updated);
            onChange(updated);
            setNewOption('');
        }
    };

    const removeOption = (index) => {
        const updated = localOptions.filter((_, i) => i !== index);
        setLocalOptions(updated);
        onChange(updated);
    };

    return (
        <div className="space-y-2">
            <div className="bg-gray-50 rounded p-2 min-h-16 border">
                <div className="flex gap-1 flex-wrap">
                    {localOptions.map((option, index) => (
                        <Tag
                            key={`${elementId}-option-${index}`}
                            closable
                            onClose={() => removeOption(index)}
                            className="mb-1"
                        >
                            {option}
                        </Tag>
                    ))}
                </div>
            </div>
            <div className="flex gap-1">
                <Input
                    size="small"
                    placeholder="Add option"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onPressEnter={addOption}
                    className="flex-1"
                />
                <InspectionBtn size="small" type="primary" onClick={addOption} icon={<PlusOutlined />} />
            </div>
        </div>
    );
};

// Forms List Component
const FormsList = ({ onCreateNew, onEditForm, onViewForm }) => {
    const [forms, setForms] = useState([]);

    const handleDelete = async (id) => {
        setForms(forms.filter(form => form.id !== id));
        await formCreationService.deleteformConfiguration(id)
        toast.success('Form deleted successfully');
    };

    const getallFormConfiguration = async () => {
        const getallformconfig = await formCreationService.getAllFormconfiguration()
        setForms(getallformconfig.data)
    }
    useEffect(() => {
        getallFormConfiguration();
    }, [])
    const handleDuplicate = (form) => {
        const newForm = {
            ...form,

            name: `${form.name} (Copy)`,
            status: 'Cloned',
            created: new Date().toISOString().split('T')[0],
            lastModified: new Date().toISOString().split('T')[0]
        };
        setForms([newForm, ...forms]);
        toast.success('Form duplicated successfully');
    };

    const columns = [
        {
            title: 'Form Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <div className="font-medium text-gray-900">{text}</div>
                    <div className="text-sm text-gray-500">{record.description}</div>
                </div>
            ), width: 100,
        },

        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'Published' ? 'green' : 'orange'}>
                    {status}
                </Tag>
            ),
            width: 100,
        },
        {
            title: 'Last Modified',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (date) => (
                <div className="text-sm text-gray-600">
                    {new Date(date).toLocaleString()}
                </div>
            ),
            width: 200,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit Form">
                        <InspectionBtn
                            type="text" size='small'

                            icon={<EditOutlined />}
                            onClick={() => onEditForm(record)}
                            className="hover:bg-green-50"
                        />
                    </Tooltip>
                    <Tooltip title="Preview Form">
                        <InspectionBtn
                            type="text" size='small'

                            icon={<EyeOutlined />}
                            onClick={() => onViewForm(record)}
                            className="hover:bg-green-50"
                        />
                    </Tooltip>
                    <Tooltip title="Duplicate Form">
                        <InspectionBtn
                            type="text"
                            size='small'
                            icon={<CopyOutlined />}
                            onClick={() => handleDuplicate(record)}
                            className="hover:bg-yellow-50"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete Form"
                        description="Are you sure you want to delete this form?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title="Delete Form">
                            <InspectionBtn
                                type="text"
                                danger size='small'

                                icon={<DeleteOutlined />}
                                className="hover:bg-red-50"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
            width: 140,
        },
    ];

    return (
        <div className="py-3 px-4 bg-gray-50 min-h-screen">
            <div className="mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm p-2 mb-3">
                    <div className="flex justify-between items-center">
                        <Title level={5}  >Form Builder</Title>
                        <InspectionBtn
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={onCreateNew}
                            size="small"
                            className="bg-green-600 hover:bg-green-700"
                        >
                            Create New Form
                        </InspectionBtn>
                    </div>
                </div>

                {/* Forms Table */}
                <Card title="All Forms" size="small" className="shadow-sm">
                    <Table
                        size='small'
                        columns={columns}
                        dataSource={forms}
                        className="spotflash-table"

                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                        }}
                    />
                </Card>
            </div>
        </div>
    );
};

// Form Builder Component
const FormBuilderPage = ({ initialData, onSave, onBack, setCurrentView }) => {
    const [formElements, setFormElements] = useState([]);
    const [commonFormElements, setCommonFormElements] = useState([]);
    const [previewMode, setPreviewMode] = useState(false);
    const [stepMeta, setStepMeta] = useState({
        name: "",
        description: "",
    });
    const { control, watch, setValue } = useForm({
        defaultValues: {
            guidance: [""], // start with one empty textbox
        },
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: "guidance",
    });
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingElement, setEditingElement] = useState(null);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [editingIsCommon, setEditingIsCommon] = useState(false);

    // Fixed: Initialize with existing data properly
    useEffect(() => {
        if (initialData) {
            setStepMeta({ name: initialData?.formMeta.tableName || '', description: initialData?.formMeta.description, });
            setValue("guidance", initialData.formMeta.guidance)
            // Handle different possible data structures
            if (initialData.nonCommonFields && Array.isArray(initialData.nonCommonFields)) {
                setFormElements(initialData.nonCommonFields.map(field => ({
                    ...field,
                    id: field.id || generateUniqueId(),
                    name: String(field.name || 'Untitled'),
                    type: String(field.type || 'textbox'),
                    placeholder: String(field.placeholder || ''),
                    required: Boolean(field.required),
                    options: Array.isArray(field.options) ? field.options : null,
                    isCommon: false
                })));
            } else if (initialData.components && Array.isArray(initialData.components)) {
                setFormElements(initialData.components.map(field => ({
                    ...field,
                    id: field.id || generateUniqueId(),
                    name: String(field.name || 'Untitled'),
                    type: String(field.type || 'textbox'),
                    placeholder: String(field.placeholder || ''),
                    required: Boolean(field.required),
                    options: Array.isArray(field.options) ? field.options : null,
                    isCommon: false
                })));
            }

            if (initialData.commonFields && Array.isArray(initialData.commonFields)) {
                setCommonFormElements(initialData.commonFields.map(field => ({
                    ...field,
                    id: field.id || generateUniqueId(),
                    name: String(field.name || 'Untitled'),
                    type: String(field.type || 'textbox'),
                    placeholder: String(field.placeholder || ''),
                    required: Boolean(field.required),
                    options: Array.isArray(field.options) ? field.options : null,
                    isCommon: true
                })));
            } else if (initialData.commonComponents && Array.isArray(initialData.commonComponents)) {
                setCommonFormElements(initialData.commonComponents.map(field => ({
                    ...field,
                    id: field.id || generateUniqueId(),
                    name: String(field.name || 'Untitled'),
                    type: String(field.type || 'textbox'),
                    placeholder: String(field.placeholder || ''),
                    required: Boolean(field.required),
                    options: Array.isArray(field.options) ? field.options : null,
                    isCommon: true
                })));
            }
        }
    }, [initialData]);

    const generateUniqueId = () => {
        return `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    };

    const createElementData = (type, isCommon = false) => {
        const elementCount = isCommon ? commonFormElements.length : formElements.length;
        const baseElement = {
            id: generateUniqueId(),
            name: `${type}_${elementCount + 1}`,
            type: type === 'numbertextbox' ? 'integer' : type,
            required: false,
            isCommon: isCommon,
            placeholder: `Enter ${type}`,
            options: ['dropdown', 'checkbox', 'radiobutton'].includes(type) ? ['Option 1', 'Option 2'] : null
        };

        if (type === 'image') {
            baseElement.placeholderimage = "";
        }

        // Ensure all values are proper types
        return {
            ...baseElement,
            name: String(baseElement.name),
            type: String(baseElement.type),
            placeholder: String(baseElement.placeholder),
            required: Boolean(baseElement.required),
            isCommon: Boolean(baseElement.isCommon)
        };
    };

    // Fixed: Improved drag end handler with better collision detection
    const handleDragEnd = (result) => {
        const { destination, source, draggableId } = result;

        if (!destination) return;

        // Add new element from sidebar
        if (source.droppableId === 'sidebar' && (destination.droppableId === 'form-area' || destination.droppableId === 'common-area')) {
            const elementType = FORM_ELEMENTS.find(el => el.id === draggableId);
            if (elementType) {
                const isCommon = destination.droppableId === 'common-area';
                const newElement = createElementData(elementType.type, isCommon);

                if (isCommon) {
                    const newCommonElements = [...commonFormElements];
                    newCommonElements.splice(destination.index, 0, newElement);
                    setCommonFormElements(newCommonElements);
                } else {
                    const newFormElements = [...formElements];
                    newFormElements.splice(destination.index, 0, newElement);
                    setFormElements(newFormElements);
                }

                toast.success(`${elementType.name} added successfully`);
            }
        }

        // Reorder within form area
        if (source.droppableId === 'form-area' && destination.droppableId === 'form-area') {
            if (source.index !== destination.index) {
                const newFormElements = [...formElements];
                const [removed] = newFormElements.splice(source.index, 1);
                newFormElements.splice(destination.index, 0, removed);
                setFormElements(newFormElements);
            }
        }

        // Reorder within common area
        if (source.droppableId === 'common-area' && destination.droppableId === 'common-area') {
            if (source.index !== destination.index) {
                const newCommonElements = [...commonFormElements];
                const [removed] = newCommonElements.splice(source.index, 1);
                newCommonElements.splice(destination.index, 0, removed);
                setCommonFormElements(newCommonElements);
            }
        }
    };

    const updateElement = (index, updates, isCommon = false) => {
        if (isCommon) {
            const newCommonElements = [...commonFormElements];
            newCommonElements[index] = { ...newCommonElements[index], ...updates };
            setCommonFormElements(newCommonElements);
        } else {
            const newFormElements = [...formElements];
            newFormElements[index] = { ...newFormElements[index], ...updates };
            setFormElements(newFormElements);
        }
    };

    const handleDeleteElement = (index, isCommon = false) => {
        Modal.confirm({
            title: 'Delete Element',
            content: 'Are you sure you want to delete this form element?',
            okText: 'Yes, Delete',
            cancelText: 'Cancel',
            okType: 'danger',
            onOk() {
                if (isCommon) {
                    const newCommonElements = commonFormElements.filter((_, i) => i !== index);
                    setCommonFormElements(newCommonElements);
                } else {
                    const newFormElements = formElements.filter((_, i) => i !== index);
                    setFormElements(newFormElements);
                }
                toast.success('Element deleted successfully');
            }
        });
    };

    const handleEditElement = (element, index, isCommon = false) => {
        setEditingElement({ ...element });
        setEditingIndex(index);
        setEditingIsCommon(isCommon);
        setEditModalVisible(true);
    };

    const handleSaveEdit = () => {
        if (editingElement && editingIndex >= 0) {
            updateElement(editingIndex, editingElement, editingIsCommon);
            setEditModalVisible(false);
            setEditingElement(null);
            setEditingIndex(-1);
            toast.success('Element updated successfully');
        }
    };

    const renderFormElement = (element, index, isCommon = false) => {

        const elementConfig = FORM_ELEMENTS.find(el => el.type === element.type || (el.type === 'integer' && element.type === 'integer'));
        if (previewMode) {
            return (
                <Col key={element?.id} xs={24} sm={24} lg={24} className="mb-3">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {String(element?.name || 'Untitled')}
                            {element.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {element?.type === 'textbox' && (
                            <Input
                                placeholder={String(element?.placeholder || `Enter ${element?.name || 'value'}`)}
                                size="small"
                            />
                        )}
                        {element?.type === 'textarea' && (
                            <TextArea
                                rows={2}
                                placeholder={String(element?.placeholder || `Enter ${element?.name || 'value'}`)}
                                size="small"
                            />
                        )}
                        {element?.type === 'integer' && (
                            <InputNumber
                                placeholder={String(`Enter ${element.name || 'number'}`)}
                                className="w-full"
                                size="small"
                            />
                        )}
                        {element?.type === 'dropdown' && (
                            <Select
                                placeholder={String(`Select ${element.name || 'option'}`)}
                                className="w-full"
                                size="small"
                            >
                                {Array.isArray(element.options) && element.options.map((option, i) => (
                                    <Option key={i} value={String(option)}>{String(option)}</Option>
                                ))}
                            </Select>
                        )}
                        {element.type === 'checkbox' && (
                            <Checkbox.Group
                                options={Array.isArray(element.options) ? element.options.map(opt => String(opt)) : []}
                                className="flex flex-col gap-1"
                            />
                        )}
                        {element.type === 'radiobutton' && (
                            <Radio.Group className="space-y-1">
                                {Array.isArray(element.options) && element.options.map((option, i) => (
                                    <Radio key={i} value={String(option)} className="block text-sm">{String(option)}</Radio>
                                ))}
                            </Radio.Group>
                        )}
                        {element.type === 'image' && (
                            <Upload
                                listType="picture-card"
                                showUploadList={false}
                                beforeUpload={() => false}
                                className="w-full"
                            >
                                <div className="flex flex-col items-center justify-center h-20">
                                    <PlusOutlined />
                                    <div className="mt-1 text-xs">Upload</div>
                                </div>
                            </Upload>
                        )}
                    </div>
                </Col>
            );
        }

        return (
            <Col key={element.id} xs={24} sm={12} lg={8} className="mb-3">
                <Draggable draggableId={element.id} index={index}>
                    {(provided, snapshot) => (
                        <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            size="small"
                            className={`transition-all duration-200 ${snapshot.isDragging ? 'shadow-lg scale-105' : 'shadow-sm hover:shadow-md'
                                } ${isCommon ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-blue-500'
                                }`}
                            bodyStyle={{ padding: '8px' }}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <div {...provided.dragHandleProps} className="cursor-move">
                                        <DragOutlined className="text-gray-400 hover:text-gray-600 text-sm" />
                                    </div>
                                    <div
                                        className="w-6 h-6 rounded flex items-center justify-center text-white text-xs"
                                        style={{ backgroundColor: elementConfig?.color || '#666' }}
                                    >
                                        {elementConfig?.icon}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-sm text-gray-900 truncate">
                                            {String(element.name || 'Untitled')}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {elementConfig?.name || 'Unknown'}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                    {element.required && <Tag color="red" size="small">Required</Tag>}
                                    <InspectionBtn
                                        type="text"
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => handleEditElement(element, index, isCommon)}
                                        className="hover:bg-green-50 p-1"
                                    />
                                    <InspectionBtn
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        size="small"
                                        onClick={() => handleDeleteElement(index, isCommon)}
                                        className="hover:bg-red-50 p-1"
                                    />
                                </div>
                            </div>

                            {/* Compact Preview */}
                            <div className="bg-gray-50 p-2 rounded text-xs">
                                {element.type === 'textbox' && (
                                    <Input placeholder={String(element.placeholder || '')} disabled size="small" />
                                )}
                                {element.type === 'textarea' && (
                                    <TextArea rows={1} placeholder={String(element.placeholder || '')} disabled size="small" />
                                )}
                                {element.type === 'integer' && (
                                    <InputNumber placeholder="0" disabled className="w-full" size="small" />
                                )}
                                {element.type === 'dropdown' && (
                                    <Select placeholder="Select..." disabled className="w-full" size="small" />
                                )}
                                {(element.type === 'checkbox' || element.type === 'radiobutton') && (
                                    <div className="text-gray-500">
                                        {Array.isArray(element.options) ? element.options.length : 0} options
                                    </div>
                                )}
                                {element.type === 'image' && (
                                    <div className="text-gray-500 text-center py-2">
                                        <FileImageOutlined /> Image Upload
                                    </div>
                                )}
                            </div>
                        </Card>
                    )}
                </Draggable>
            </Col>
        );
    };

    const handleSave = async () => {

        if (!stepMeta.name.trim()) {
            toast.error('Please enter a form name');
            return;
        }

        const formObject = {
            name: stepMeta.name,
            description: stepMeta.description,
            guidance: watch("guidance"),

            nonCommonFields: formElements.map(element => ({
                id: element.id,
                name: element.name,
                type: element.type,
                required: element.required || false,
                guidance_text: element.guidance_text || "",

                isCommon: false,
                placeholder: element.placeholder || '',
                ...(element.type === 'image' ? { placeholderimage: element.placeholderimage || "" } : {}),
                options: element.options
            })),
            commonFields: commonFormElements.map(element => ({
                id: element.id,
                name: element.name,
                type: element.type,
                required: element.required || false,
                isCommon: true,
                guidance_text: element.guidance_text || "",

                placeholder: element.placeholder || '',
                ...(element.type === 'image' ? { placeholderimage: element.placeholderimage || "" } : {}),
                options: element.options
            }))
        };

        if (initialData && initialData?.status !== "Cloned") {
            await formCreationService.updateformConfiguration(initialData.id, formObject)
        }
        else {
            await formCreationService.addFormConfiguration(formObject)
        }
        setCurrentView("list")
        toast.success('Form saved successfully!');
    };

    return (
        <div className="h-screen bg-gray-50">
            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex h-full">
                    {/* Sidebar */}
                    <div className="w-64 bg-white shadow-sm border-r border-gray-200 flex-shrink-0 flex flex-col">
                        <div className="p-4 border-b border-gray-200">
                            <InspectionBtn
                                icon={<ArrowLeftOutlined />}
                                onClick={onBack}
                                className="mb-3"
                                type="text"
                            >
                                Back to Forms
                            </InspectionBtn>
                            <Title level={5} className="mb-3 ">Form Builder</Title>

                            <div className='flex flex-col gap-2'>
                                <div>
                                    <Typography.Text className="mb-1">Module Name</Typography.Text>

                                    <Input
                                        placeholder="Enter form name"
                                        value={stepMeta.name}
                                        onChange={(e) => setStepMeta(prev => ({ ...prev, name: e.target.value }))}
                                        size="small"
                                    />
                                </div>
                                <div>
                                    <Typography.Text level={5} className="mb-1">Description</Typography.Text>

                                    <Input.TextArea rows={2}
                                        placeholder="Enter form Description"
                                        value={stepMeta.description}
                                        onChange={(e) => setStepMeta(prev => ({ ...prev, description: e.target.value }))}

                                        size="small"
                                    />
                                </div>

                            </div>
                        </div>

                        <div className="flex-1 overflow-hidden p-4">
                            <Text type="secondary" className="block text-xs mb-3">
                                Drag elements to build your form
                            </Text>

                            <Droppable droppableId="sidebar" isDropDisabled={true}>
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="space-y-2 overflow-y-auto h-full"
                                    >
                                        {FORM_ELEMENTS.map((element, index) => (
                                            <Draggable
                                                key={element.id}
                                                draggableId={element.id}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={`
                                                            flex items-center gap-3 p-2 border rounded cursor-move
                                                            transition-all duration-200 bg-white hover:shadow-sm
                                                            ${snapshot.isDragging ? 'shadow-md scale-105' : ''}
                                                        `}
                                                        style={{
                                                            borderLeftColor: element.color,
                                                            borderLeftWidth: '3px',
                                                            ...provided.draggableProps.style
                                                        }}
                                                    >
                                                        <div
                                                            className="w-6 h-6 rounded flex items-center justify-center text-white text-sm"
                                                            style={{ backgroundColor: element.color }}
                                                        >
                                                            {element.icon}
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-700">
                                                            {element.name}
                                                        </span>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>

                        <div className="p-4 border-t border-gray-200">
                            <InspectionBtn
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                className="w-full mb-2"
                                disabled={!stepMeta.name.trim()}
                                size="small"
                            >
                                Save Form
                            </InspectionBtn>
                            <InspectionBtn
                                type={previewMode ? "primary" : "default"}
                                icon={<EyeOutlined />}
                                onClick={() => setPreviewMode(!previewMode)}
                                className="w-full"
                                size="small"
                            >
                                {previewMode ? "Edit Mode" : "Preview"}
                            </InspectionBtn>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="bg-white shadow-sm border-b px-6 py-3 flex flex-col gap-3 flex-shrink-0">
                            {/* Top row: icon + title + mode + tags */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <FormOutlined className="text-blue-500" />
                                    <div className='flex '>
                                        <Title level={5} className="mb-0">
                                            {stepMeta.name || "Untitled Form"}
                                        </Title>
                                        <Text type="secondary" className="text-x ms-3">
                                            {previewMode ? "Preview Mode" : "Edit Mode"}
                                        </Text>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Tag color="blue">{formElements.length} Fields</Tag>
                                    <Tag color="green">{commonFormElements.length} Common</Tag>
                                </div>
                            </div>

                            {/* Bottom row: Guidance section */}
                            <div>
                                <div className='flex justify-between'>
                                    <Typography.Text className="mb-2 block font-medium">Guidance</Typography.Text>
                                    <InspectionBtn size="small" onClick={() => append("")}>
                                        + Add Guidance
                                    </InspectionBtn>
                                </div>
                                {fields.map((field, index) => (
                                    <div key={field.id} className="flex items-center mb-2 gap-2">
                                        <Controller
                                            name={`guidance.${index}`}
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    placeholder={`Guidance ${index + 1}`}
                                                    size="small"
                                                />
                                            )}
                                        />
                                        <InspectionBtn
                                            type="text"
                                            danger
                                            size="small"
                                            onClick={() => remove(index)}
                                            disabled={fields.length === 1}
                                        >
                                            Remove
                                        </InspectionBtn>
                                    </div>
                                ))}

                            </div>
                        </div>


                        {/* Form Areas - Fixed: Better container sizing for drag and drop */}
                        <div className="flex-1 p-4 space-y-4" style={{ height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
                            {/* Regular Fields */}
                            <Card size='small'
                                title={
                                    <div className="flex items-center justify-between">
                                        <Text strong>Form Fields</Text>
                                        <Tag color="blue">{formElements.length} elements</Tag>
                                    </div>
                                }
                                className="shadow-sm"
                                bodyStyle={{ padding: '16px' }}
                            >
                                <Droppable droppableId="form-area">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`transition-all duration-200 ${snapshot.isDraggingOver
                                                ? 'bg-green-50 border-2 border-dashed border-blue-300 rounded-lg p-2'
                                                : ''
                                                }`}
                                            style={{
                                                minHeight: '200px',
                                                // Fixed: Ensure proper dimensions for drop detection
                                                position: 'relative',
                                                width: '100%'
                                            }}
                                        >
                                            {formElements.length === 0 ? (
                                                <div className="text-center text-gray-400 py-12">
                                                    <FormOutlined className="text-3xl mb-2 text-gray-300" />
                                                    <div className="text-sm">
                                                        Drag form elements here to create your form
                                                    </div>
                                                </div>
                                            ) : (
                                                <Row gutter={[12, 12]}>
                                                    {formElements.map((element, index) =>
                                                        renderFormElement(element, index, false)
                                                    )}
                                                </Row>
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </Card>

                            {/* Common Fields */}
                            <Card size='small'
                                title={
                                    <div className="flex items-center justify-between">
                                        <Text strong>Common Fields</Text>
                                        <Tag color="green">{commonFormElements.length} elements</Tag>
                                    </div>
                                }
                                className="shadow-sm border-green-200"
                                bodyStyle={{ padding: '16px' }}
                            >
                                <Droppable droppableId="common-area">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`transition-all duration-200 ${snapshot.isDraggingOver
                                                ? 'bg-green-50 border-2 border-dashed border-green-300 rounded-lg p-2'
                                                : ''
                                                }`}
                                            style={{
                                                minHeight: '200px',
                                                // Fixed: Ensure proper dimensions for drop detection
                                                position: 'relative',
                                                width: '100%'
                                            }}
                                        >
                                            {commonFormElements.length === 0 ? (
                                                <div className="text-center text-gray-400 py-12">
                                                    <FormOutlined className="text-3xl mb-2 text-green-300" />
                                                    <div className="text-sm">
                                                        Drop elements here to create reusable common fields
                                                    </div>
                                                </div>
                                            ) : (
                                                <Row gutter={[12, 12]}>
                                                    {commonFormElements.map((element, index) =>
                                                        renderFormElement(element, index, true)
                                                    )}
                                                </Row>
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </Card>
                        </div>
                    </div>

                    {/* Edit Element Modal */}
                    <Modal
                        title="Edit Form Element"
                        open={editModalVisible}
                        onOk={handleSaveEdit}
                        onCancel={() => {
                            setEditModalVisible(false);
                            setEditingElement(null);
                        }}
                        width={500}
                        okText="Save Changes"
                        cancelText="Cancel"
                    >
                        {editingElement && (
                            <Form layout="vertical">
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="Element Name">
                                            <Input
                                                value={editingElement.name}
                                                onChange={(e) => setEditingElement({
                                                    ...editingElement,
                                                    name: e.target.value
                                                })}
                                                placeholder="Enter element name"
                                                size="small"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="Type">
                                            <Input
                                                value={editingElement.type}
                                                disabled
                                                className="bg-gray-50"
                                                size="small"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row  >
                                    <Col span={24}>
                                        <Form.Item label="Guidance">
                                            <Input
                                                value={editingElement.guidance_text}
                                                onChange={(e) => setEditingElement({
                                                    ...editingElement,
                                                    guidance_text: e.target.value
                                                })}
                                                placeholder="Enter element name"
                                                size="small"
                                            />
                                        </Form.Item>
                                    </Col>

                                </Row>

                                <Form.Item label="Placeholder Text">
                                    <Input
                                        value={editingElement.placeholder}
                                        onChange={(e) => setEditingElement({
                                            ...editingElement,
                                            placeholder: e.target.value
                                        })}
                                        placeholder="Enter placeholder text"
                                        size="small"
                                    />
                                </Form.Item>

                                <Form.Item>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={editingElement.required}
                                            onChange={(checked) => setEditingElement({
                                                ...editingElement,
                                                required: checked
                                            })}
                                            size="small"
                                        />
                                        <Text>Required field</Text>
                                    </div>
                                </Form.Item>

                                {/* Image Upload for Image Type */}
                         {editingElement.type === 'image' && (
  <Form.Item label="Placeholder Image">
    <Upload
      accept="image/*"
      showUploadList={false}
      beforeUpload={(file) => {
        // Validate file type
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
          toast.error('Please upload an image file!');
          return false;
        }

        // Validate file size (optional - limit to 5MB)
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
          toast.error('Image must be smaller than 5MB!');
          return false;
        }

        // Convert to base64
        const reader = new FileReader();
        reader.onload = (e) => {
          // e.target.result contains the complete base64 string with data URI prefix
          const base64String = e.target.result;
          console.log('Base64 string:', base64String); // For debugging
          
          setEditingElement({
            ...editingElement,
            placeholderimage: base64String, // This should be: "data:image/jpeg;base64,/9j/4AAQ..."
          });
          toast.success('Image uploaded successfully');
        };
        
        reader.onerror = () => {
          toast.error('Error reading file');
        };
        
        reader.readAsDataURL(file);
        return false; // Prevent default upload behavior
      }}
    >
      <InspectionBtn icon={<UploadOutlined />} size="small" className="w-full">
        {editingElement.placeholderimage ? 'Change Image' : 'Upload Image'}
      </InspectionBtn>
    </Upload>

    {editingElement.placeholderimage && (
      <div className="mt-2">
        <img
          src={editingElement.placeholderimage}
          alt="preview"
          className="w-full h-24 object-cover rounded border"
          onError={(e) => {
            console.error('Error loading image:', e);
            toast.error('Error displaying image');
          }}
        />
        <div className="mt-1 text-xs text-gray-500">
          Size: {Math.round(editingElement.placeholderimage.length * 0.75 / 1024)} KB
        </div>
        <InspectionBtn 
          size="small" 
          danger 
          type="text"
          onClick={() => setEditingElement({
            ...editingElement,
            placeholderimage: ""
          })}
          className="mt-1"
        >
          Remove Image
        </InspectionBtn>
      </div>
    )}
  </Form.Item>
)}

                                {/* Options for Dropdown, Checkbox, Radio */}
                                {['dropdown', 'checkbox', 'radiobutton'].includes(editingElement.type) && (
                                    <Form.Item label="Options">
                                        <OptionsManager
                                            options={editingElement.options}
                                            onChange={(options) => setEditingElement({
                                                ...editingElement,
                                                options
                                            })}
                                            elementId={editingElement.id}
                                        />
                                    </Form.Item>
                                )}

                                {/* Compact Preview */}
                                <Form.Item label="Preview">
                                    <div className="bg-gray-50 p-3 rounded border">
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {editingElement.name}
                                                {editingElement.required && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                            {editingElement.type === 'textbox' && (
                                                <Input
                                                    placeholder={editingElement.placeholder || `Enter ${editingElement.name}`}
                                                    disabled
                                                    size="small"
                                                />
                                            )}
                                            {editingElement.type === 'textarea' && (
                                                <TextArea
                                                    rows={2}
                                                    placeholder={editingElement.placeholder || `Enter ${editingElement.name}`}
                                                    disabled
                                                    size="small"
                                                />
                                            )}
                                            {editingElement.type === 'integer' && (
                                                <InputNumber
                                                    placeholder={`Enter ${editingElement.name}`}
                                                    disabled
                                                    className="w-full"
                                                    size="small"
                                                />
                                            )}
                                            {editingElement.type === 'dropdown' && (
                                                <Select
                                                    placeholder={`Select ${editingElement.name}`}
                                                    disabled
                                                    className="w-full"
                                                    size="small"
                                                >
                                                    {editingElement.options?.map((option, i) => (
                                                        <Option key={i} value={option}>{option}</Option>
                                                    ))}
                                                </Select>
                                            )}
                                            {editingElement.type === 'checkbox' && (
                                                <Checkbox.Group
                                                    options={editingElement.options}
                                                    disabled
                                                />
                                            )}
                                            {editingElement.type === 'radiobutton' && (
                                                <Radio.Group disabled>
                                                    {editingElement.options?.map((option, i) => (
                                                        <Radio key={i} value={option} className="block text-sm">{option}</Radio>
                                                    ))}
                                                </Radio.Group>
                                            )}
                                            {editingElement.type === 'image' && (
                                                <div className="border-2 border-dashed border-gray-300 p-4 text-center rounded">
                                                    {editingElement.placeholderimage ? (
                                                        <img
                                                            src={editingElement.placeholderimage}
                                                            alt="placeholder"
                                                            className="w-full h-20 object-cover rounded"
                                                        />
                                                    ) : (
                                                        <>
                                                            <FileImageOutlined className="text-xl text-gray-400 mb-1" />
                                                            <div className="text-sm">Image Upload</div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Form.Item>
                            </Form>
                        )}
                    </Modal>
                </div>
            </DragDropContext>
        </div>
    );
};

// Main App Component
export default function App() {
    const [currentView, setCurrentView] = useState('list'); // 'list' or 'builder'
    const [editingForm, setEditingForm] = useState(null);

    const handleCreateNew = () => {
        setEditingForm(null);
        setCurrentView('builder');
    };

    const handleEditForm = (form) => {
        setEditingForm(form);
        setCurrentView('builder');
    };

    const handleViewForm = (form) => {
        // For now, just switch to builder in preview mode
        setEditingForm(form);
        setCurrentView('builder');
    };

    const handleSaveForm = (formData) => {
        setCurrentView('list');
    };

    const handleBackToList = () => {
        setCurrentView('list');
        setEditingForm(null);
    };

    return (
        <div className="h-screen overflow-hidden">
            {currentView === 'list' ? (
                <FormsList
                    onCreateNew={handleCreateNew}
                    onEditForm={handleEditForm}
                    onViewForm={handleViewForm}
                />
            ) : (
                <FormBuilderPage
                    initialData={editingForm}
                    onSave={handleSaveForm}
                    onBack={handleBackToList}
                    setCurrentView={setCurrentView}
                />
            )}
        </div>
    );
}